// dashboard/frontend/src/components/Campaign/CampaignList.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    Box,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    FileCopy as CopyIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const CampaignList = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [duplicating, setDuplicating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
        try {
            console.log('Fetching campaigns...'); // Debug log
            const response = await api.get('/campaigns/');
            console.log('Fetched campaigns:', response.data); // Debug log
            setCampaigns(response.data.results);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async (id) => {
        setDuplicating(true);
        try {
            const response = await api.post(`/campaigns/${id}/duplicate/`);
            // await fetchCampaigns();
            setCampaigns([...campaigns, response.data]);
        } catch (error) {
            console.error('Error duplicating campaign:', error);
        } finally {
            setDuplicating(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this campaign?')) {
            try {
                await api.delete(`/campaigns/${id}/`);
                setCampaigns(campaigns.filter(camp => camp.id !== id));
            } catch (error) {
                console.error('Error deleting campaign:', error);
            }
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" m={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">Campaigns</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/campaigns/new')}
                >
                    New Campaign
                </Button>
            </Box>

            <Grid container spacing={3}>
                {campaigns.map((campaign) => (
                    <Grid item xs={12} md={6} key={campaign.id}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {campaign.name}
                                </Typography>
                                
                                <Typography color="textSecondary" gutterBottom>
                                    {campaign.description}
                                </Typography>
                                
                                <Box mt={2} display="flex" gap={1}>
                                    <Chip
                                        label={`${campaign.contacts_count} Contacts`}
                                        size="small"
                                    />
                                    <Chip
                                        label={`${campaign.emails_sent} Sent`}
                                        size="small"
                                    />
                                    <Chip
                                        label={`${campaign.emails_opened} Opened`}
                                        size="small"
                                    />
                                    <Chip
                                        label={campaign.status}
                                        color={campaign.status === 'active' ? 'success' : 'default'}
                                        size="small"
                                    />
                                    {campaign.rounds_count > 1 && (
                                        <Chip
                                            label={`${campaign.rounds_count} Rounds`}
                                            color="info"
                                            size="small"
                                            icon={<HistoryIcon />}
                                        />
                                    )}                                    
                                </Box>
                            </CardContent>
                            
                            <CardActions>
                                <IconButton 
                                    size="small"
                                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton 
                                    size="small"
                                    onClick={() => handleDuplicate(campaign.id)}
                                    disabled={duplicating}
                                >
                                    {duplicating ? <CircularProgress size={20} /> : <CopyIcon />}
                                </IconButton>
                                <IconButton 
                                    size="small"
                                    onClick={() => handleDelete(campaign.id)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default CampaignList;